import { IApplicationData } from 'src/interfaces/ApplicationData';
import { CDN_PROVIDER, DRM_PROVIDERS, EXTENSION_TYPES, TRANSMISSION_TYPE } from 'src/interfaces/enums';
import { IPlayerAsset } from 'src/interfaces/PlayerTemplate';
import { IDRMSource, IVideoJSPlayer } from 'src/interfaces/VideoJS';
import DRMService from 'src/services/DRMService';
import { pallyconLicenseEndpoint } from 'src/services/DRMService/constants';
import MuxService from 'src/services/MuxService';
import { detectIOS, detectSafari } from 'src/utils/browserUtils';
import videojs from 'video.js';
import VideoJsConfigBuilder from '../VideoJsConfigBuilder';

class VideoJsConfigDirector {
  applicationData: IApplicationData;
  constructor(applicationData: IApplicationData) {
    this.applicationData = applicationData;
  }

  buildVideoJsConfig() {
    const builder = new VideoJsConfigBuilder();

    return builder
      .withCredentials(this.isWithCredentials())
      .sources(this.extractSources())
      .plugins(this.getPlugins())
      .withEncryptedUrl(this.isEncryptedUrl())
      .build();
  }

  extractSources(): videojs.Tech.SourceObject[] {
    const { mediaAssets, isDrmEnabled } = this.applicationData;

    const sources = (mediaAssets || []).map((asset) => ({
      src: this.getSource(asset),
      type: asset.contentType,
      withCredentials: this.isWithCredentials(),
      objectKey: asset.objectKey || '',
    }));

    if (isDrmEnabled) return [this.getDRMSource(sources)];

    return sources.slice(0, 1);
  }

  getSource(asset: IPlayerAsset) {
    return this.isEncryptedUrl() ? asset.urlEncrypted : asset.url;
  }

  isWithCredentials() {
    const { transmissionType, mediaAssets, cdnProvider } = this.applicationData;
    const isLive = transmissionType === TRANSMISSION_TYPE.live;

    if (isLive || !mediaAssets[0]) return false;
    return cdnProvider === CDN_PROVIDER.CLOUDFRONT;
  }

  getPlugins() {
    return {
      mux: MuxService.getConfig(this.applicationData),
    };
  }

  isEncryptedUrl() {
    const { isDrmEnabled, transmissionType, flags } = this.applicationData;
    const isLive = transmissionType === TRANSMISSION_TYPE.live;

    if (!flags?.ENABLE_PLAYER_V4_ENCRYPTION || !this.isOverrideNative()) {
      return false;
    }

    return !isLive && !isDrmEnabled && this.isCloudFrontCdn();
  }

  isCloudFrontCdn() {
    const { cdnProvider } = this.applicationData;

    return cdnProvider === CDN_PROVIDER.CLOUDFRONT;
  }

  isOverrideNative() {
    const videoJS = videojs as unknown as IVideoJSPlayer;
    return (!detectIOS() && !detectSafari()) || !videoJS.Vhs.supportsNativeHls;
  }

  getDRMSource(sources: videojs.Tech.SourceObject[]) {
    const keySystem = DRMService.selectKeySystem();
    const source: IDRMSource = this.getSourceType(sources, keySystem) || sources[0];
    const licenseURL = pallyconLicenseEndpoint(this.applicationData);
    source.keySystems = DRMService.getKeySystems(licenseURL, this.applicationData.clubMembershipId);

    return source;
  }

  getSourceType(sources: videojs.Tech.SourceObject[], keySystem: DRM_PROVIDERS) {
    if (keySystem === DRM_PROVIDERS.WIDEVINE || keySystem === DRM_PROVIDERS.PLAYREADY) {
      return sources.find((source) => source.src.includes(EXTENSION_TYPES.DASH));
    }

    return sources.find((source) => source.src.includes(EXTENSION_TYPES.HLS));
  }
}

export default VideoJsConfigDirector;
